import * as React from 'react'
import { useState } from 'react'
import { Button, Menu, MenuList, MenuPopover, MenuTrigger, Text, Tooltip } from '@fluentui/react-components'
import {
    AlbumFilled,
    AlbumRegular,
    Building20Regular,
    BuildingMultiple20Regular,
    bundleIcon,
    DocumentTableFilled,
    DocumentTableRegular,
    Person20Regular,
    ShieldPerson20Regular,
} from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { BaseOverviewLineItemProps, useOverviewStyles } from '../../../pages/BaseOverview/BaseOverview'
import { isPartyOverviewItem, ListItemDto, PartyOverviewItemDto } from '../../../config/partyConfig'
import { PartyType } from '../../../components/Form/Party/PartyForm'
import useEntity from '../../../hooks/useEntity'
import { IOverviewItemDto } from '../../../config/fileConfig'
import { PartyFilterType, PartySortingType, useItemStyles } from './BasePartyOverview'
import useSubmitDocumentGenerationRequest from '../../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import { DocumentGenerationType } from '../../../hooks/document/useDocumentGenerationHandler'
import { PartyCardReportDto } from '../../../components/Form/Finance/Report/PartyCardReportForm'
import PersonAvatar from '../../../components/PersonAvatar'
import PhoneNumberButton from '../../components/Button/PhoneNumberButton'
import EmailButton from '../../components/Button/EmailButton'
import { composeEmailClick } from '../../../helpers/partyComposeHelper'

const AlbumIcon = bundleIcon(AlbumFilled, AlbumRegular)
const PartCardReportIcon = bundleIcon(DocumentTableFilled, DocumentTableRegular)

export const getPartyIcon = (party: ListItemDto): React.JSX.Element => {
    if (isPartyOverviewItem(party) && (party as PartyOverviewItemDto).identificationVerified) {
        return <ShieldPerson20Regular />
    }

    switch (party.type) {
        case PartyType.Legal: {
            return party.crmRelation ? <BuildingMultiple20Regular /> : <Building20Regular />
        }
        case PartyType.Natural: {
            return <Person20Regular />
        }
    }

    return undefined
}

export function PartyListItem(props: BaseOverviewLineItemProps<PartyOverviewItemDto, PartyFilterType, PartySortingType>) {
    const itemStyles = useItemStyles()
    const overviewStyles = useOverviewStyles()
    const submitRequest = useSubmitDocumentGenerationRequest<PartyCardReportDto>(DocumentGenerationType.PartyCard)

    const { t: translate } = useTranslation()

    const [showMenu, setShowMenu] = useState<boolean>(false)

    const [files, , resetFiles] = useEntity<IOverviewItemDto[]>(
        () => ({ url: '/file', params: { parties: [props.item.id] } }),
        showMenu ? false : undefined,
        result => result.data,
        undefined,
        undefined,
        [showMenu],
    )

    return (
        <>
            {props.item.accountManager && <PersonAvatar name={props.item.accountManager.fullName} color={'colorful'} size={24} />}
            <div>{getPartyIcon(props.item)}</div>
            <Text className={itemStyles.listPartyItemOverflow}>{props.item.displayName || <em>Naam ontbreekt.</em>}</Text>
            <aside data-hover-actions={'display'} className={overviewStyles.flexGapXS}>
                {props.item.type === PartyType.Legal && (
                    <Tooltip relationship={'label'} content={translate('party.report.partycard')} withArrow>
                        <Button
                            icon={<PartCardReportIcon />}
                            appearance="secondary"
                            size={'small'}
                            onClick={() => submitRequest({ partyId: props.item.id })}
                        />
                    </Tooltip>
                )}
                <Menu
                    positioning={{ autoSize: true }}
                    open={showMenu}
                    onOpenChange={(_, { open }) => {
                        setShowMenu(open)
                        if (!open) {
                            resetFiles()
                        }
                    }}
                >
                    <MenuTrigger disableButtonEnhancement>
                        <Tooltip relationship={'label'} content={translate('party.file.dialog')} withArrow>
                            <Button icon={<AlbumIcon />} appearance="secondary" size={'small'} onClick={() => setShowMenu(true)} />
                        </Tooltip>
                    </MenuTrigger>

                    <MenuPopover>
                        <MenuList className={itemStyles.listPartyItemFileList}>
                            {showMenu && files.loaded && files.data?.length === 0 && (
                                <Text key={-1} size={200}>
                                    {translate('base.empty')}
                                </Text>
                            )}
                            {showMenu &&
                                files.data?.map((file, index) => (
                                    <div key={index} className={itemStyles.listPartyItemFileItem}>
                                        <Link to={`/file/${file.id}`}>
                                            <Text size={200}>{file.reference}</Text>
                                        </Link>
                                        <Text size={200}>{file.name.substring(0, 50)}</Text>
                                    </div>
                                ))}
                        </MenuList>
                    </MenuPopover>
                </Menu>
            </aside>
            <div data-hover-contact className={itemStyles.listPartyItemContact}>
                {props.item.phoneNumber && <PhoneNumberButton clickValue={props.item.phoneNumber} withTooltip />}
                {props.item.email && <EmailButton tooltipValue={props.item.email} onClick={() => composeEmailClick(props.item)()} withTooltip />}
            </div>
        </>
    )
}
